import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/lib/reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { submitBookingForm } from "@/lib/site";
import { useLanguage } from "@/context/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const empty = { first_name: "", last_name: "", phone: "", service: "", date: "", time: "", message: "" };

export const Booking = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const serviceOptions = t.booking?.servicesList || (t.services.items || []).map((s) => s.title);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone || !form.service) {
      toast.error(t.booking.errorFill);
      return;
    }
    setLoading(true);
    try {
      const result = await submitBookingForm(form);
      if (result.success) {
        setDone(true);
        setForm(empty);
        toast.success(t.booking.successDesc);
      } else {
        throw new Error(result.error || "Ndodhi një gabim.");
      }
    } catch (err) {
      toast.error(err.message || "Ndodhi një gabim. Provoni përsëri.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 bg-background border border-input rounded-sm text-sm text-foreground placeholder:text-muted-foreground focus:border-accent transition-colors";

  return (
    <section id="booking" data-testid="booking" className="py-24 md:py-32">
      <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal className="overline font-bold tracking-[0.2em]">{t.booking.overline}</Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-foreground leading-[1.1]">
              {t.booking.title}
            </h2>
          </Reveal>
          
          {/* Standout Free Consultation Banner */}
          <Reveal delay={0.08}>
            <div className="mt-6 p-5 rounded-sm border-2 border-accent/40 bg-accent/10 shadow-sm relative overflow-hidden">
              <div className="flex items-start gap-3.5">
                <div className="h-10 w-10 shrink-0 rounded-full bg-accent text-white grid place-items-center shadow-md shadow-accent/30 font-bold">
                  <Sparkles size={20} />
                </div>
                <div>
                  <div className="text-base font-bold text-foreground tracking-tight">
                    {t.booking.bannerTitle}
                  </div>
                  <p className="mt-1 text-sm text-foreground/80 font-medium leading-relaxed">
                    {t.booking.bannerDesc}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="mt-6 text-foreground/80 font-medium leading-relaxed max-w-sm text-base sm:text-lg">
              {t.booking.desc}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {done ? (
              <div data-testid="booking-success" className="flat-card p-10 md:p-14 text-center">
                <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-accent text-white shadow-md shadow-accent/30">
                  <Check size={26} />
                </div>
                <h3 className="mt-6 text-2xl font-light tracking-tight text-foreground">{t.booking.successTitle}</h3>
                <p className="mt-3 text-muted-foreground">
                  {t.booking.successDesc}
                </p>
                <button
                  data-testid="booking-new"
                  onClick={() => setDone(false)}
                  className="mt-8 text-sm text-accent font-medium link-underline"
                >
                  {t.booking.sendAnother}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form" className="flat-card p-8 md:p-10 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">{t.booking.firstName}</label>
                  <input data-testid="booking-first-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder={t.booking.firstName.replace(" *", "")} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">{t.booking.lastName}</label>
                  <input data-testid="booking-last-name" className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder={t.booking.lastName.replace(" *", "")} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">{t.booking.phone}</label>
                  <input data-testid="booking-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+355 6X XXX XXXX" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">{t.booking.service}</label>
                  <Select value={form.service} onValueChange={(v) => set("service", v)}>
                    <SelectTrigger data-testid="booking-service" className="h-12 rounded-sm border-input bg-background text-sm">
                      <SelectValue placeholder={t.booking.servicePlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`booking-service-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">{t.booking.date}</label>
                  <input type="date" data-testid="booking-date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">{t.booking.time}</label>
                  <input type="time" data-testid="booking-time" className={inputCls} value={form.time} onChange={(e) => set("time", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground mb-2 block">{t.booking.message}</label>
                  <textarea data-testid="booking-message" rows={4} className="w-full px-4 py-3 bg-background border border-input rounded-sm text-sm text-foreground placeholder:text-muted-foreground focus:border-accent transition-colors resize-none" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t.booking.messagePlaceholder} />
                </div>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="sm:col-span-2 inline-flex items-center justify-center gap-2 h-13 py-4 rounded-sm bg-accent text-white text-sm font-medium hover:bg-accent/90 shadow-md shadow-accent/25 transition-all duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {loading ? t.booking.submitting : t.booking.submitBtn}
                </motion.button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
